import React, { useState, useEffect, useMemo } from "react";
import { User, TournamentResult, TournamentEvent, ApiTournamentResult } from "../../types";
import { Button } from "./Button";
import { getListUsers } from "../../api";
import { UserSelect } from "./form/UserSelect";

interface RankingEditorProps {
  event: TournamentEvent;
  initialResults?: TournamentResult[];
  onSave: (results: Partial<ApiTournamentResult>[]) => void;
  onCancel: () => void;
}

export const RankingEditor: React.FC<RankingEditorProps> = ({
  event,
  initialResults = [],
  onSave,
  onCancel,
}) => {
    const [obj_results, setObjResults] = useState<Record<number, Partial<ApiTournamentResult>[]>>({});
    const [results, setResults] = useState<Partial<ApiTournamentResult>[]>([]);
    const [users, setUsers] = useState<User[]>([]);

  // init results
  useEffect(() => {
    const fetchItems = async () => {
      const data = await getListUsers(event.season.id);
      setUsers(data);
    };
    fetchItems();
    setResults(initialResults.map(r => ({...r, event_id: r.event.id, user_id: r.user.id})));
  }, [event.season.id, initialResults]);

  // ricarico obj_results al change di results
  useEffect(() => {
    let obj_results_tmp: Record<number, Partial<ApiTournamentResult>[]> = {};
    for (let i = 0; i < results.length; i++)  {
      const result = results[i];
      if (! result.position) continue;
      if (! obj_results_tmp[result.position]) obj_results_tmp[result.position] = [];
      obj_results_tmp[result.position].push(result);
    }
    setObjResults(obj_results_tmp);
  }, [results]);

  const updateResult = (index: number, update: Partial<ApiTournamentResult>) => {
    const copy = [...results];
    copy[index] = { ...copy[index], ...update };
    setResults(copy);
  };

  const movePosition = (position_from: number, dir: number) => {
    const copy = [...results];
    const position_to = position_from + dir;
    if (position_to < 1 || position_to > Object.keys(obj_results).length) return;

    for (let i = 0; i < copy.length; i++) {
      const result = copy[i];
      if (result.position === position_from) {
        result.position = position_to;
      } else if (result.position === position_to) {
        result.position = position_from
      }
    }
    setResults(copy);
  };

  const addResult = (addToPosition?: number) => {
    const position: number = addToPosition ?? Object.keys(obj_results).length + 1;
    const newResult: Partial<ApiTournamentResult> = {
        event_id: event.id,
        user_id: 0,
        position: position,
    }
    setResults([...results, newResult]);
  };

  const removePosition = (positionToRemove: number) => {
    const copy = results.filter((res, i) => res.position !== positionToRemove);
    for (let i = 0; i < copy.length; i++) {
      const result = copy[i];
      if (!result || !result.position) continue;
      if (result.position > positionToRemove) {
        result.position -= 1;
      }
    }
    setResults(copy);
  };

  const removeResult = (indexToRemove: number) => {
    setResults(results.filter((res, i) => i !== indexToRemove));
  };

  const handleSave = () => {
    onSave(results); 
  };

  const users_id_selected = useMemo(() => {
    return users.filter(u => results.some(r => r.user_id === u.id)).map(u => u.id);
  }, [users, results]);
  const sorted_keys = useMemo(() => {
    return Object.keys(obj_results).map(k => Number(k)).sort((x, y) => x - y);
  }, [obj_results]) 
  const cells_numbers = useMemo(() => {
    let count = 0;
    for (let i = 0; i < sorted_keys.length; i++) {
      count += obj_results[sorted_keys[i]].length;
    }
    return count;
  }, [obj_results, sorted_keys]) 

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-end bg-black/50">
      <div className="bg-white rounded-t-xl w-full max-w-md animate-slideUp max-h-[90vh] flex flex-col">

        {/* HEADER fisso */}
        <div className="p-4">
          <h2 className="text-xl font-bold">Gestisci classifica</h2>
        </div>

        {/* BODY scrollable */}
        <div className="flex-1 overflow-y-auto px-4">
          <div className="space-y-4">
            { sorted_keys.map((position, _) => {
              const res = obj_results[position] || [];
              return (
                <div key={position} className="p-3 border border-gray-300 rounded shadow flex flex-col space-y-2 bg-white">
                    <div className="flex justify-between items-center">
                      <span className="font-bold">#{position}</span>
                      <div className="space-x-2">
                        <Button size="sm" variant="success" onClick={() => addResult(position)}>+</Button>
                        <Button size="sm" onClick={() => movePosition(position, -1)}>↑</Button>
                        <Button size="sm" onClick={() => movePosition(position, 1)}>↓</Button>
                        <Button size="sm" variant="destructive" onClick={() => removePosition(position)}>X</Button>
                      </div>
                    </div>
                    {res.map((r, i_user) => (
                      <div className="flex justify-between items-center">
                        { i_user >= 1 && (
                          <Button size="sm" variant="destructive" onClick={() => {
                            const i_r: number = results.indexOf(r);
                            removeResult(i_r)
                          }}>-</Button>
                        )}
                        <UserSelect
                          className={i_user >= 1 ? "ml-2" : ""}
                          users={users}
                          value={r.user_id ?? null}
                          emptyOptionLabel="Seleziona utente"
                          // Gli utenti già presenti in classifica restano visibili
                          // ma non riselezionabili (tranne quello di questa riga).
                          disabledUserIds={users_id_selected.filter((id) => id !== r.user_id)}
                          onChange={(userId) => {
                            const i_r: number = results.indexOf(r);
                            updateResult(i_r, {
                              user_id: userId ?? 0
                            })
                          }}
                        />
                      </div>
                    ))}
                </div>
              )
            }
          )
        }
      </div>
        </div>

        {/* FOOTER fisso */}
        <div className="p-4 flex justify-between space-x-2">
          <Button variant="success" onClick={() => addResult()} 
              disabled={cells_numbers >= users.length}
          >+ Aggiungi</Button>
          <div className="space-x-2">
            <Button variant="secondary" onClick={onCancel}>Annulla</Button>
            <Button onClick={handleSave}>Salva</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
